import { validateShape } from "./schema.js";
export const STORAGE_KEY = "weekkeep.web.v1";
export const dayKey = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
export const parseDay = (key) => new Date(`${key}T12:00:00`);
export const validDay = (key) =>
  typeof key === "string" &&
  /^\d{4}-\d{2}-\d{2}$/.test(key) &&
  Number.isFinite(+parseDay(key)) &&
  dayKey(parseDay(key)) === key;
export const addDays = (key, amount) => {
  const date = parseDay(key);
  date.setDate(date.getDate() + amount);
  return dayKey(date);
};
// Use civil dates, not elapsed local milliseconds: daylight saving must not shift weeks.
export const dayDistance = (a, b) =>
  Math.round(
    (Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000,
  );
export const weekIndex = (birth, date) =>
  Math.max(0, Math.floor(dayDistance(birth, date) / 7));
export const weekStart = (birth, date) =>
  addDays(birth, weekIndex(birth, date) * 7);
export const ageAt = (birth, date = dayKey()) =>
  Number(date.slice(0, 4)) -
  Number(birth.slice(0, 4)) -
  (date.slice(5) < birth.slice(5) ? 1 : 0);
export const anniversary = (birth, age) => {
  const d = parseDay(birth);
  const month = d.getMonth();
  d.setFullYear(d.getFullYear() + age);
  if (d.getMonth() !== month) d.setDate(0);
  return dayKey(d);
};
export const emptyState = () => ({
  version: 3,
  profile: null,
  entries: [],
  lived: {},
  closures: {},
  compass: { values: "", calls: "", chapter: "", experiment: "" },
  acknowledged: [],
});
export function saveEntry(
  state,
  { id, date, title = "", text = "", memory = "", location },
  today = dayKey(),
) {
  if (!validDay(date) || date < state.profile.birthDate)
    throw new Error("Choose a date within your life.");
  if (
    ![title, text, memory].every((x) => typeof x === "string") ||
    !(text.trim() || memory.trim())
  )
    throw new Error("Write a thought or a memory line first.");
  const next = structuredClone(state);
  const existing = id ? next.entries.find((e) => e.id === id) : null;
  if (id && !existing) throw new Error("This entry no longer exists.");
  const entry = {
    id: id || crypto.randomUUID(),
    date,
    title: title.trim(),
    text,
    memory: memory.trim(),
    createdAt: existing?.createdAt || new Date().toISOString(),
  };
  if (location !== undefined) entry.location = location;
  else if (existing?.location !== undefined) entry.location = existing.location;
  if (existing) next.entries[next.entries.indexOf(existing)] = entry;
  else next.entries.push(entry);
  if (date <= today) next.lived[date] = true;
  return next;
}
export function toggleLived(state, date, today = dayKey()) {
  if (!validDay(date) || date > today || date < state.profile.birthDate)
    throw new Error("Only days already lived can be marked.");
  const next = structuredClone(state);
  next.lived[date] = !next.lived[date];
  return next;
}
export function validateState(value) {
  validateShape(value);
  const p = value.profile;
  if (p.birthDate > dayKey() || p.birthDate < "1900-01-01")
    throw new Error("Birth date must be between 1900 and today.");
  const ids = new Set();
  for (const e of value.entries) {
    if (
      e.date < p.birthDate ||
      ids.has(e.id) ||
      !(e.text.trim() || e.memory.trim())
    )
      throw new Error(
        "An entry has an invalid date, duplicate ID, or no writing.",
      );
    ids.add(e.id);
  }
  // Do not invalidate historical archives when the clock/time zone moves backwards.
  // The no-future-completion rule is enforced at the transition, against its clock.
  for (const key of Object.keys(value.lived))
    if (key < p.birthDate) throw new Error("A day mark predates the profile.");
  for (const [key, c] of Object.entries(value.closures))
    if (key < p.birthDate || !c.memory.trim())
      throw new Error("Invalid weekly reflection.");
  return structuredClone(value);
}
export function migrate(value) {
  if (!value || !Number.isInteger(value.version))
    throw new Error("The archive has no schema version.");
  if (value.version > 3)
    throw new Error(
      "This archive was created by a newer Weekkeep. Update the app before opening it.",
    );
  if (value.version === 1) {
    // A pure, deterministic migration: preserve every entry, timestamp and mark.
    value = { ...structuredClone(value), version: 2 };
  }
  if (value.version === 2) value = { ...structuredClone(value), version: 3 };
  // Only the exact historical empty envelope is accepted before onboarding.
  if (
    value.profile === null &&
    JSON.stringify(value) === JSON.stringify(emptyState())
  )
    return structuredClone(value);
  return validateState(value);
}
export function reduceJournal(state, event, today = dayKey()) {
  let next;
  if (event.type === "ONBOARD") {
    if (state.profile) throw new Error("Onboarding is already complete.");
    next = { ...emptyState(), profile: event.profile };
  } else {
    validateState(state);
    next = structuredClone(state);
    switch (event.type) {
      case "SAVE_ENTRY":
        next = saveEntry(state, event.entry, today);
        break;
      case "TOGGLE_LIVED":
        next = toggleLived(state, event.date, today);
        break;
      case "DELETE_ENTRY":
        if (!next.entries.some((e) => e.id === event.id))
          throw new Error("Entry no longer exists.");
        next.entries = next.entries.filter((e) => e.id !== event.id);
        break;
      case "SAVE_COMPASS":
        next.compass = event.compass;
        break;
      case "CLOSE_WEEK":
        if (
          !validDay(event.start) ||
          event.start > today ||
          weekStart(state.profile.birthDate, event.start) !== event.start
        )
          throw new Error("Choose a week already started.");
        next.closures[event.start] = {
          memory: event.memory.trim(),
          experiment: event.experiment.trim(),
        };
        if (event.carry) next.compass.experiment = event.experiment.trim();
        break;
      case "REOPEN_WEEK":
        delete next.closures[event.start];
        break;
      case "ACKNOWLEDGE":
        next.acknowledged = [...new Set([...next.acknowledged, event.id])];
        break;
      case "RESTORE":
        next = migrate(event.archive);
        break;
      default:
        throw new Error(`Unsupported journal event: ${event.type}`);
    }
  }
  return validateState(next);
}
